import { createContext, useContext } from "react";
import { PageType } from "@/constants/PageType";
import usePresets from "@/hooks/presets/usePresets";
import { useRememberedSelection } from "@/hooks/userSettings/useRememberedSelection";
import { Preset } from "@/models/Preset";

interface PresetContextProps {
  presets: Preset[];
  selectedPreset: Preset | null;
  setSelectedPreset: (next: Preset | null) => void;
}

const PresetContext = createContext<PresetContextProps | undefined>(undefined);

export const PresetProvider: React.FC<{
  children: React.ReactNode;
  initialPresets?: Preset[];
  /**
   * Names whose preset selection this is, e.g. `faultMonitor.preset`.
   *
   * Required, because every page used to share one unscoped browser key: the fault overview and
   * the diagnostic monitor overwrote each other's choice, and so did two Geotab users of the
   * same browser profile.
   */
  scope: string;
}> = ({ children, initialPresets, scope }) => {
  const { data: fetchedPresets = [] } = usePresets();
  const presets = initialPresets ?? fetchedPresets;
  const { selected: selectedPreset, setSelected: setSelectedPreset } = useRememberedSelection(
    scope,
    presets,
  );

  return (
    <PresetContext.Provider
      value={{
        presets,
        selectedPreset,
        setSelectedPreset,
      }}
    >
      {children}
    </PresetContext.Provider>
  );
};

export const usePresetContext = (pageType?: PageType) => {
  const context = useContext(PresetContext);
  if (pageType === "maintenance") {
    return {
      presets: [],
      selectedPreset: { id: "maintenance", name: "Maintenance", columns: [] } as Preset,
      setSelectedPreset: () => {},
    };
  }

  if (!context) {
    throw new Error("usePresetContext must be used within a PresetProvider");
  }
  return context;
};
